/* eslint-disable no-use-before-define */
/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import WizardContainer from './components/core/WizardContainer';
import { transformModelToFormData, transformFormDataToModel } from './config/wizardConfig';
import { extractSiteAndLocationFromNode } from '../Hierarchy/helpers';
import { wizardModes, formFieldNames } from './constants';
const PowertrainWizardComponentV2 = ({
    show,
    close,
    model,
    actions,
    selectedNodeData,
    hierarchyData
}) => {
    const { t: translate } = useTranslation();
    const operationInitiatedRef = useRef(false);

    // Extract key properties from model
    const {
        powertrainID,
        powertrainName,
        selectedSite,
        selectedOrganization,
        addedAssets,
        isEditMode,
        sites,
        // Redux state for assets
        availableAssets,
        isLoadingAssets,
        assetsError,
        // Redux state for asset subtypes
        assetSubTypes,
        isLoadingAssetSubTypes,
        assetSubTypesError,
        // Redux state for asset details
        assetDetails,
        isLoadingAssetDetails,
        assetDetailsError,
        // Redux state for powertrain operations
        isAddingPowertrain,
        isEditingPowertrain,
        powertrainOperationError,
        lastOperationSuccess,
        // Selected powertrain from configuration
        selectedPowertrain
    } = model;

    const {
        addPowertrainRequest,
        editPowertrainRequest,
        clearStore,
        getAvailableAssetsRequest,
        getAssetSubTypesRequest,
        getAssetDetailsRequest
    } = actions;

    // Determine mode and create existing model for both edit and create modes
    const wizardMode = isEditMode ? wizardModes.EDIT : wizardModes.CREATE;

    // Extract site and location from selected node in hierarchy if available
    const hierarchyOrganizationData = hierarchyData?.data || hierarchyData;
    const extractedNodeData = selectedNodeData && hierarchyOrganizationData
        ? extractSiteAndLocationFromNode(selectedNodeData, hierarchyOrganizationData)
        : { site: null, location: null };

    // Extract pre-selected asset IDs from hierarchy selection
    const preSelectedAssetIds = selectedNodeData?.preSelectedAssetIds || [];

    const existingModel = isEditMode ? {
        powertrainID,
        powertrainName,
        selectedSite,
        [formFieldNames.SELECTED_LOCATION]: model?.selectedLocation ||
            (selectedPowertrain?.locationId ? {
                locationId: selectedPowertrain?.locationId,
                locationName: selectedPowertrain?.locationName,
            } : null),
        selectedOrganization,
        [formFieldNames.ADDED_ASSETS]: addedAssets || [],
        sites: sites || [],
        rowVersion: model?.rowVersion,
        selectedPowertrain
    } : {
        // Also pass data for create mode when it exists (for tests with pre-populated state)
        // Or use extracted data from selected node in hierarchy
        powertrainName: powertrainName || '',
        selectedSite: selectedSite || extractedNodeData?.site || null,
        [formFieldNames.SELECTED_LOCATION]: extractedNodeData?.location || null,
        selectedOrganization: selectedOrganization || null,
        [formFieldNames.ADDED_ASSETS]: addedAssets || [],
        sites: sites || [],
        preSelectedAssetIds
    };

    const handleComplete = (formData) => {

        // Check if we received API payload data directly (new approach from WizardContainer)
        let modelData;
        if (formData[formFieldNames.REVIEW] && typeof formData[formFieldNames.REVIEW] === 'object' &&
            (formData[formFieldNames.REVIEW][formFieldNames.NAME] || formData[formFieldNames.REVIEW][formFieldNames.POWERTRAIN_NAME])) {
            // The review step contains API payload data, use it directly
            modelData = formData[formFieldNames.REVIEW];
        } else if (formData[formFieldNames.NAME] || formData[formFieldNames.POWERTRAIN_NAME]) {
            // The formData itself is the API payload (new direct approach)
            modelData = formData;
        } else {
            // Transform form data back to model format (fallback)
            modelData = transformFormDataToModel(formData);
        }

        // Only allow one operation at a time
        if (isAddingPowertrain || isEditingPowertrain) {
            return;
        }

        // Mark that an operation has been initiated
        operationInitiatedRef.current = true;

        if (isEditMode) {
            editPowertrainRequest(modelData);
        } else {
            addPowertrainRequest(modelData);
        }

        // Don't close immediately - let the Redux state handle success/error
        // The component will close when the operation completes successfully
    };

    const handleCancel = useCallback(() => {
        close();
        clearStore();
    }, [close, clearStore]);

    // Monitor powertrain operation status and close wizard on success ONLY
    useEffect(() => {
        // Only proceed if an operation was initiated and has completed
        if (operationInitiatedRef.current && !isAddingPowertrain && !isEditingPowertrain) {
            operationInitiatedRef.current = false; // Reset flag

            if (lastOperationSuccess === true) {
                // Operation completed successfully - close the wizard
                handleCancel();
            }
            // If lastOperationSuccess === false, keep wizard open so user can see error and retry
        }
    }, [isAddingPowertrain, isEditingPowertrain, lastOperationSuccess, powertrainOperationError, handleCancel]);

    if (!show) {
        return null;
    }

    return (
        <WizardContainer
            mode={wizardMode}
            existingModel={existingModel}
            onComplete={handleComplete}
            onCancel={handleCancel}
            actions={actions}
            sites={sites}
            className='powertrain-wizard-v2'
            // Pass Redux state for assets
            availableAssets={availableAssets}
            isLoadingAssets={isLoadingAssets}
            assetsError={assetsError}
            // Pass Redux state for asset subtypes
            assetSubTypes={assetSubTypes}
            isLoadingAssetSubTypes={isLoadingAssetSubTypes}
            assetSubTypesError={assetSubTypesError}
            // Pass Redux state for asset details
            assetDetails={assetDetails}
            isLoadingAssetDetails={isLoadingAssetDetails}
            assetDetailsError={assetDetailsError}
            // Pass Redux state for powertrain operations
            isAddingPowertrain={isAddingPowertrain}
            isEditingPowertrain={isEditingPowertrain}
            powertrainOperationError={powertrainOperationError}
            // Full hierarchy for location dropdown in BasicInfoStep
            hierarchyData={hierarchyData}
            organizationId={selectedOrganization?.organizationID || null}
            preSelectedAssetIds={preSelectedAssetIds}
        />
    );
};

PowertrainWizardComponentV2.propTypes = {
    show: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,
    model: PropTypes.shape({
        powertrainID: PropTypes.number,
        powertrainName: PropTypes.string,
        selectedOrganization: PropTypes.shape({
            organizationID: PropTypes.number,
            name: PropTypes.string
        }),
        selectedSite: PropTypes.shape({
            siteId: PropTypes.number,
            siteName: PropTypes.string,
        }),
        addedAssets: PropTypes.array,
        sites: PropTypes.array,
        isEditMode: PropTypes.bool,
        rowVersion: PropTypes.string,
        selectedLocation: PropTypes.object,
        selectedPowertrain: PropTypes.object,
        // Redux state for assets
        availableAssets: PropTypes.array,
        isLoadingAssets: PropTypes.bool,
        assetsError: PropTypes.object,
        // Redux state for asset subtypes
        assetSubTypes: PropTypes.array,
        isLoadingAssetSubTypes: PropTypes.bool,
        assetSubTypesError: PropTypes.object,
        // Redux state for asset details
        assetDetails: PropTypes.array,
        isLoadingAssetDetails: PropTypes.bool,
        assetDetailsError: PropTypes.object,
        // Redux state for powertrain operations
        isAddingPowertrain: PropTypes.bool,
        isEditingPowertrain: PropTypes.bool,
        powertrainOperationError: PropTypes.object,
        lastOperationSuccess: PropTypes.bool,
    }).isRequired,
    actions: PropTypes.shape({
        addPowertrainRequest: PropTypes.func.isRequired,
        editPowertrainRequest: PropTypes.func.isRequired,
        clearStore: PropTypes.func.isRequired,
        getAvailableAssetsRequest: PropTypes.func.isRequired,
        getAssetSubTypesRequest: PropTypes.func.isRequired,
        getAssetDetailsRequest: PropTypes.func.isRequired,
    }).isRequired,
    hierarchyData: PropTypes.shape({
        data: PropTypes.object
    }),
    selectedNodeData: PropTypes.shape({
        preSelectedAssetIds: PropTypes.array
    })
};

export default PowertrainWizardComponentV2;
